/**
 * <DailyBriefingPlayer /> — podcast-style Read-Aloud queue for /things-to-know.
 *
 * Given a list of news items (the current top-5), reads each aloud in
 * sequence using the browser's SpeechSynthesis API. Handles play/pause,
 * skip forward, skip back, and stop. Persists a small progress card so the
 * listener knows which story is playing without having to visually track.
 *
 * Uses the same sessionStorage cache as <TranslatableText /> — if a
 * translation for the user's preferred language is already cached for a
 * given summary, we read the translated version instead of the original.
 * (We never fire a new /i18n/translate call from the player — the goal is
 * "listen to the day's stories in your language IF they've been translated".)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Headphones, Play, Pause, SkipForward, SkipBack, Square, Rss, Copy, Check } from "lucide-react";
import { getPreferredLang, PREF_LANG_EVENT } from "@/components/LanguageChip";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const PODCAST_FEED_URL = `${BACKEND_URL}/api/news/briefing/podcast.xml`;

function getCachedTranslation(target, text) {
  if (!text || !target || target === "en") return null;
  try {
    const s = sessionStorage.getItem(`i18n:${target}::${text}`);
    return s || null;
  } catch { return null; }
}

function pickVoice(lang) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  const short = (lang || "").toLowerCase().split("-")[0];
  return (
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(short + "-")) ||
    voices.find((v) => v.lang && v.lang.toLowerCase() === short) ||
    null
  );
}

export default function DailyBriefingPlayer({ items = [], max = 5, testid = "daily-briefing" }) {
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [index, setIndex] = useState(0);
  const [lang, setLang] = useState(getPreferredLang());
  const utteranceRef = useRef(null);
  const cancelledRef = useRef(false);

  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
  const queue = items.slice(0, max);

  // React to language switches from the header globe pill.
  useEffect(() => {
    const h = () => setLang(getPreferredLang());
    window.addEventListener(PREF_LANG_EVENT, h);
    return () => window.removeEventListener(PREF_LANG_EVENT, h);
  }, []);

  // Some browsers load voices asynchronously — nudge the list.
  useEffect(() => {
    if (!ttsSupported) return;
    const ping = () => window.speechSynthesis.getVoices();
    ping();
    window.speechSynthesis.addEventListener("voiceschanged", ping);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", ping);
  }, [ttsSupported]);

  // Always stop speech when the component unmounts.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    };
  }, []);

  const textFor = useCallback((item) => {
    if (!item) return "";
    const summary = item.ai_summary || item.short_description || "";
    // Prefer a cached translation if the user has already translated this text.
    const cached = getCachedTranslation(lang, summary);
    const spokenSummary = cached || summary;
    // Read the title first, then the summary, so listeners know what's coming.
    return `${item.title}. ${spokenSummary}`;
  }, [lang]);

  const langForItem = useCallback(() => {
    // If the preferred language is set and we have a cached translation, the
    // spoken text will be in that language. Otherwise assume the source
    // (English for our current news pipeline).
    return lang && lang !== "auto" ? lang : "en";
  }, [lang]);

  const speakAt = useCallback((i) => {
    if (!ttsSupported) return;
    if (i < 0 || i >= queue.length) {
      setPlaying(false);
      setPaused(false);
      return;
    }
    cancelledRef.current = false;
    const item = queue[i];
    const utterance = new SpeechSynthesisUtterance(textFor(item));
    utterance.lang = langForItem();
    utterance.rate = 0.98;
    utterance.pitch = 1;
    const voice = pickVoice(utterance.lang);
    if (voice) utterance.voice = voice;
    utterance.onstart = () => { setPlaying(true); setPaused(false); };
    utterance.onerror = () => { if (!cancelledRef.current) setPlaying(false); };
    utterance.onend = () => {
      if (cancelledRef.current) return;
      // Small 300ms breath, then auto-advance.
      setTimeout(() => {
        if (cancelledRef.current) return;
        const next = i + 1;
        if (next < queue.length) {
          setIndex(next);
          speakAt(next);
        } else {
          setPlaying(false);
          setPaused(false);
        }
      }, 300);
    };
    utteranceRef.current = utterance;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    try { window.speechSynthesis.speak(utterance); } catch { setPlaying(false); }
  }, [queue, ttsSupported, textFor, langForItem]);

  const play = () => {
    if (!queue.length) return;
    setExpanded(true);
    if (paused) {
      try { window.speechSynthesis.resume(); } catch { /* ignore */ }
      setPaused(false);
      setPlaying(true);
      return;
    }
    if (playing) return;
    speakAt(index);
  };

  const pause = () => {
    try { window.speechSynthesis.pause(); } catch { /* ignore */ }
    setPaused(true);
    setPlaying(false);
  };

  const stop = () => {
    cancelledRef.current = true;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setPlaying(false);
    setPaused(false);
    setIndex(0);
    setExpanded(false);
  };

  const next = () => {
    if (index + 1 >= queue.length) return;
    cancelledRef.current = true;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const newIdx = index + 1;
    setIndex(newIdx);
    setTimeout(() => speakAt(newIdx), 100);
  };

  const prev = () => {
    if (index === 0) return;
    cancelledRef.current = true;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const newIdx = index - 1;
    setIndex(newIdx);
    setTimeout(() => speakAt(newIdx), 100);
  };

  if (!ttsSupported || queue.length === 0) return null;
  const currentItem = queue[index];

  // Podcast subscribe helpers.
  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(PODCAST_FEED_URL);
      toast.success("Podcast feed URL copied");
    } catch { toast.error("Couldn't copy — long-press the link to copy manually."); }
  };
  const feedNoProto = PODCAST_FEED_URL.replace(/^https?:\/\//, "");
  const appleUrl = `podcast://${feedNoProto}`;
  const overcastUrl = `https://overcast.fm/podcasterlogin?feedurl=${encodeURIComponent(PODCAST_FEED_URL)}`;

  // Compact button when idle, expanded control panel while playing.
  return (
    <div
      data-testid={testid}
      className="mb-6 rounded-2xl border overflow-hidden transition-all"
      style={{
        borderColor: expanded ? "var(--wofy-primary)" : "var(--wofy-line)",
        background: expanded ? "linear-gradient(180deg, #FDF6E9 0%, #F1E6D0 100%)" : "var(--wofy-paper, #FDFBF7)",
      }}
    >
      {!expanded ? (
        <button
          type="button"
          onClick={play}
          className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:opacity-90"
          data-testid={`${testid}-start`}
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full" style={{ background: "var(--wofy-primary)", color: "white" }}>
              <Headphones className="w-4 h-4" />
            </span>
            <div>
              <div className="font-display text-base">Play daily briefing</div>
              <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                Listen to the top {queue.length} stories · ~{Math.max(2, queue.length)} min · in your language
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--wofy-primary)" }}>
            <Play className="w-4 h-4" /> Play
          </div>
        </button>
      ) : (
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full" style={{ background: "var(--wofy-primary)", color: "white" }}>
              <Headphones className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--wofy-primary)" }}>
                DAILY BRIEFING · STORY {index + 1} OF {queue.length}
              </div>
              <div className="font-display text-base leading-snug truncate">
                {currentItem?.title}
              </div>
            </div>
          </div>
          {/* Progress dots */}
          <div className="flex items-center gap-1 mb-3">
            {queue.map((_, i) => (
              <div
                key={i}
                className="h-1 flex-1 rounded-full transition-all"
                style={{
                  background: i < index ? "var(--wofy-primary)" : i === index ? (playing ? "var(--wofy-primary)" : "#c47f3a") : "rgba(0,0,0,0.1)",
                  opacity: i === index && playing ? 1 : 0.7,
                }}
              />
            ))}
          </div>
          {/* Controls */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={index === 0}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border disabled:opacity-30"
              style={{ borderColor: "var(--wofy-line)" }}
              title="Previous story"
              data-testid={`${testid}-prev`}
            >
              <SkipBack className="w-4 h-4" />
            </button>
            {playing ? (
              <button
                type="button"
                onClick={pause}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full"
                style={{ background: "var(--wofy-primary)", color: "white" }}
                title="Pause"
                data-testid={`${testid}-pause`}
              >
                <Pause className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={play}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full"
                style={{ background: "var(--wofy-primary)", color: "white" }}
                title={paused ? "Resume" : "Play"}
                data-testid={`${testid}-play`}
              >
                <Play className="w-5 h-5 ml-0.5" />
              </button>
            )}
            <button
              type="button"
              onClick={next}
              disabled={index + 1 >= queue.length}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border disabled:opacity-30"
              style={{ borderColor: "var(--wofy-line)" }}
              title="Next story"
              data-testid={`${testid}-next`}
            >
              <SkipForward className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={stop}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text)" }}
              title="Stop and close"
              data-testid={`${testid}-stop`}
            >
              <Square className="w-3 h-3" /> Stop
            </button>
          </div>
        </div>
      )}
      {/* Podcast subscribe strip — always visible below the player. */}
      <div
        className="flex flex-wrap items-center gap-2 px-4 py-2 text-xs border-t"
        style={{ borderColor: "var(--wofy-line)", background: "rgba(255,255,255,0.4)" }}
        data-testid={`${testid}-subscribe`}
      >
        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: "var(--wofy-primary)" }}>
          <Rss className="w-3.5 h-3.5" /> Subscribe
        </span>
        <span style={{ color: "var(--wofy-text-soft)" }}>Get the briefing daily in your podcast app:</span>
        <a
          href={appleUrl}
          className="underline hover:opacity-80"
          style={{ color: "var(--wofy-text)" }}
          data-testid={`${testid}-subscribe-apple`}
        >
          Apple Podcasts
        </a>
        <span style={{ color: "var(--wofy-text-soft)" }}>·</span>
        <a
          href={overcastUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
          style={{ color: "var(--wofy-text)" }}
          data-testid={`${testid}-subscribe-overcast`}
        >
          Overcast
        </a>
        <span style={{ color: "var(--wofy-text-soft)" }}>·</span>
        <a
          href={PODCAST_FEED_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
          style={{ color: "var(--wofy-text)" }}
          data-testid={`${testid}-subscribe-rss`}
        >
          RSS
        </a>
        <button
          type="button"
          onClick={copyFeed}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full border hover:bg-black/[0.04]"
          style={{ borderColor: "var(--wofy-line)" }}
          title="Copy feed URL"
          data-testid={`${testid}-subscribe-copy`}
        >
          <Copy className="w-3 h-3" /> Copy feed
        </button>
      </div>
    </div>
  );
}
